import {in_array} from "@/functions/array/in_array";
import {empty} from "@/functions/var/empty";
import {ModalMdWidth} from "@/globalConfig";
import {DIALOG_MESSAGE} from "@/redux/action/type.action";
import {GetState, SetState} from "@/redux/reduxhelper";
import IModal from "@/shared/Modal/IModal";
import FlexGrid from "@components/flexGrid/FlexGrid";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import {DialogActions, Stack, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useLocation} from "react-router";

const MESSAGE_TYPES = ['error', 'success', 'info'];

const DialogMessage = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const receivedMessage = GetState(DIALOG_MESSAGE, {
        type: null,
        message: null,
        closeCallback: undefined,
        closeBtnText: '',
        hideCloseBtn: false,
        runCallbackOnClose: false,
        hideDialogIcon: false,
        actions: [],
    });

    const {message, type, actions: rawActions} = receivedMessage;

    const closeBtnText = empty(receivedMessage?.closeBtnText) ? t('t_close') : receivedMessage?.closeBtnText;
    const actions = Array.isArray(rawActions) ? rawActions : [];

    const [open, setOpen] = useState(false);
    const [data, setData] = useState({});

    const closeDialogHandler = useCallback(() => {
        try {
            if (receivedMessage?.closeCallback)
                if (receivedMessage?.runCallbackOnClose)
                    receivedMessage?.closeCallback?.();

        } catch (e) {
            // console.log(e)
        } finally {
            setOpen(false)
        }
    }, [receivedMessage]);

    useEffect(() => {
        const isValidType = in_array(receivedMessage?.type, MESSAGE_TYPES);
        if (isValidType) {
            setData({type, message});
        }
        setOpen(isValidType);
    }, [receivedMessage]);

    useEffect(() => {
        if (!open) dispatch(SetState(DIALOG_MESSAGE, {type: null}));
    }, [open, dispatch]);

    const handleActionClick = useCallback((action) => {
        action?.onClick?.();
        if (action?.closeOnClick !== false) closeDialogHandler();
    }, [closeDialogHandler]);

    const handleCloseClick = useCallback(() => {
        closeDialogHandler();
        receivedMessage?.closeCallback?.();
    }, [closeDialogHandler, receivedMessage]);

    const location = useLocation();
    useEffect(() => {
        closeDialogHandler();
    }, [location]);

    return (
        <IModal
            sx={{zIndex: 99999}}
            open={open}
            maxWidth={ModalMdWidth}
            width={'90%'}
            handleClose={closeDialogHandler}
            aria-describedby="alert-dialog-slide-description"
            showClose={true}
        >
            <Stack alignItems={'center'} gap={2} sx={{mt: 2, width: '100%'}}>

                {!receivedMessage?.hideDialogIcon &&
                 <>
                     {data?.type === 'error' &&
                      <ErrorOutlineIcon fontSize={'large'} color={data?.type}/>
                     }
                     {data?.type === 'success' &&
                      <CheckCircleOutlineIcon fontSize={'large'} color={data?.type}/>
                     }
                     {data?.type === 'info' &&
                      <InfoIcon fontSize={'large'} color={'info'}/>
                     }
                 </>
                }

                <FlexGrid gap={1} sx={{width: 'calc(100% - 32px)'}}>
                    {(typeof data?.message === 'string') ?
                        (<Typography sx={{width: '100%'}} className={'body-normal txt-center'}>
                            {data?.message}
                        </Typography>) : (data?.message)
                    }
                </FlexGrid>
            </Stack>

            <DialogActions sx={{justifyContent: 'center', my: 3, gap: 1.5, flexWrap: 'wrap'}}>
                {!receivedMessage?.hideCloseBtn &&
                 <Button className={'border-radius-large btn-border-success txt-success btn-small'}
                         sx={{minWidth: 175}}
                         variant={'outlined'}
                         onClick={handleCloseClick}>
                     {closeBtnText}
                 </Button>
                }

                {actions.map((action, idx) => (
                    <Button
                        key={action.key ?? idx}
                        className={action.className ?? 'border-radius-large btn-large'}
                        sx={{minWidth: 180, ...action.sx}}
                        variant={action.variant ?? 'contained'}
                        color={action.color ?? 'primary'}
                        disabled={action.disabled}
                        onClick={() => handleActionClick(action)}
                    >
                        {action.text}
                    </Button>
                ))}
            </DialogActions>
        </IModal>
    );
};

export default React.memo(DialogMessage);